import type {
  CreateSupplierPayload,
  UpdateSupplierPayload,
} from "@/modules/inventory/domain/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  inventorySuppliersApi,
  type SuppliersListParams,
} from "./suppliers-client";

export const suppliersKeys = {
  all: ["inventory", "suppliers"] as const,
  list: (params: SuppliersListParams) =>
    ["inventory", "suppliers", "list", params] as const,
  lookup: ["inventory", "suppliers", "lookup"] as const,
  detail: (id: string) => ["inventory", "suppliers", "detail", id] as const,
};

export function useSuppliersQuery(params: SuppliersListParams = {}) {
  return useQuery({
    queryKey: suppliersKeys.list(params),
    queryFn: () => inventorySuppliersApi.list(params),
  });
}

export function useSuppliersLookupQuery() {
  return useQuery({
    queryKey: suppliersKeys.lookup,
    queryFn: () => inventorySuppliersApi.lookup(),
  });
}

export function useSupplierQuery(id: string) {
  return useQuery({
    queryKey: suppliersKeys.detail(id),
    queryFn: () => inventorySuppliersApi.get(id),
    enabled: !!id,
  });
}

export function useCreateSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateSupplierPayload) =>
      inventorySuppliersApi.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: suppliersKeys.all });
    },
  });
}

export function useUpdateSupplier(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateSupplierPayload) =>
      inventorySuppliersApi.update(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: suppliersKeys.detail(id) });
      qc.invalidateQueries({ queryKey: suppliersKeys.all });
    },
  });
}

export function useDeactivateSupplier() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => inventorySuppliersApi.deactivate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: suppliersKeys.all });
    },
  });
  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });
  return { ...base, run };
}
